import React, { useRef, useEffect, useState } from 'react';
import { Platform, View, Text, StyleSheet, Pressable } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';

// Use named imports so webpack resolves the package's exported ESM entrypoints
// instead of treating the package root like a CommonJS namespace.
import { Map as MapLibreMapClass, Marker, type StyleSpecification } from 'maplibre-gl';
// CSS injected at runtime by style-loader; no TS declarations for .css files.
// @ts-ignore
import 'maplibre-gl/dist/maplibre-gl.css';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface MapMarker {
  id: string;
  latitude: number;
  longitude: number;
  /** Tooltip label shown in a popup on hover */
  label?: string;
  /** Pin hex color, default '#004895' */
  color?: string;
}

export interface MapLibreMapProps {
  latitude: number;
  longitude: number;
  /** MapLibre zoom level (0 = world, 20 = building). Default 4 */
  zoom?: number;
  /** Container height in px. Default 500 */
  height?: number;
  /** Optional heading text rendered above the map */
  label?: string;
  /** Optional fixed home/reset latitude (defaults to current latitude) */
  homeLatitude?: number;
  /** Optional fixed home/reset longitude (defaults to current longitude) */
  homeLongitude?: number;
  /** Optional fixed home/reset zoom (defaults to current zoom) */
  homeZoom?: number;
  markers?: MapMarker[];
  /** Optional marker id to visually emphasize */
  highlightedMarkerId?: string | null;
  /** Called when a marker pin is clicked */
  onMarkerPress?: (id: string) => void;
  /** Called when a marker is actually hovered */
  onMarkerHover?: (id: string, position: { x: number; y: number }) => void;
  /** Called when the hover target leaves the map or is no longer near a marker */
  onMarkerLeave?: () => void;
}

// ─── Internal helper ──────────────────────────────────────────────────────────

function addMapMarkers(
  map: MapLibreMapClass,
  markers: MapMarker[],
  highlightedMarkerId?: string | null,
  onMarkerPress?: (id: string) => void,
  onMarkerHover?: (id: string, position: { x: number; y: number }) => void,
  onMarkerLeave?: () => void
): globalThis.Map<string, Marker> {
  const added = new globalThis.Map<string, Marker>();
  const mapContainer = map.getContainer();
  markers.forEach(m => {
    try {
      const isHighlighted = highlightedMarkerId != null && highlightedMarkerId === m.id;
      const markerElement = document.createElement('div');
      markerElement.dataset.lmMarkerId = m.id;
      markerElement.style.display = 'flex';
      markerElement.style.flexDirection = 'column';
      markerElement.style.alignItems = 'center';
      markerElement.style.transform = 'translateY(-8px)';
      markerElement.style.cursor = 'pointer';
      markerElement.style.pointerEvents = 'auto';

      if (isHighlighted) {
        const halo = document.createElement('div');
        halo.style.position = 'absolute';
        halo.style.width = '30px';
        halo.style.height = '30px';
        halo.style.borderRadius = '50%';
        halo.style.background = 'rgba(0, 92, 188, 0.20)';
        halo.style.border = '1px solid rgba(0, 92, 188, 0.55)';
        halo.style.transform = 'translateY(-1px)';
        halo.style.pointerEvents = 'none';
        markerElement.appendChild(halo);
      }

      const pin = document.createElement('div');
      pin.style.width = isHighlighted ? '22px' : '18px';
      pin.style.height = isHighlighted ? '22px' : '18px';
      pin.style.borderRadius = '50% 50% 50% 0';
      pin.style.background = m.color ?? '#004895';
      pin.style.transform = 'rotate(-45deg)';
      pin.style.boxShadow = isHighlighted
        ? '0 4px 10px rgba(0, 0, 0, 0.35)'
        : '0 2px 6px rgba(0, 0, 0, 0.28)';
      pin.style.border = isHighlighted ? '3px solid #FFFFFF' : '2px solid #FFFFFF';
      pin.style.position = 'relative';
      pin.style.zIndex = '2';

      markerElement.appendChild(pin);

      if (m.label) {
        const label = document.createElement('div');
        // Hook for the global dark-mode stylesheet (see globalDarkModeWeb.ts)
        label.className = 'lh-map-marker-label';
        label.textContent = m.label;
        label.style.marginTop = '4px';
        label.style.padding = '2px 6px';
        label.style.borderRadius = '999px';
        label.style.background = 'rgba(255, 255, 255, 0.96)';
        label.style.border = '1px solid rgba(0, 0, 0, 0.12)';
        label.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.16)';
        label.style.color = '#17324D';
        label.style.fontSize = '11px';
        label.style.fontWeight = '600';
        label.style.lineHeight = '14px';
        label.style.whiteSpace = 'nowrap';
        label.style.maxWidth = '160px';
        label.style.overflow = 'hidden';
        label.style.textOverflow = 'ellipsis';
        label.style.pointerEvents = 'none';
        markerElement.appendChild(label);
      }

      const marker = new Marker({ element: markerElement })
        .setLngLat([m.longitude, m.latitude]);
      marker.addTo(map);

      if (onMarkerHover) {
        marker.getElement().addEventListener('mouseenter', () => {
          const projected = map.project([m.longitude, m.latitude]);
          const containerRect = mapContainer.getBoundingClientRect();
          onMarkerHover(m.id, {
            x: containerRect.left + projected.x,
            y: containerRect.top + projected.y,
          });
        });
        marker.getElement().addEventListener('mouseleave', () => {
          onMarkerLeave?.();
        });
      }

      if (onMarkerPress) {
        marker.getElement().addEventListener('click', () => onMarkerPress(m.id));
      }
      added.set(m.id, marker);
    } catch { /* skip markers with bad / missing coordinates */ }
  });
  return added;
}


/**
 * MapLibreMap — Web-only interactive map using MapLibre GL + OSM raster tiles.
 *
 * On native iOS / Android a graceful fallback `<View>` is rendered instead.
 *
 * Requirements (already applied to this repo):
 *   • npm install maplibre-gl  (maplibre-gl@6.4.0 installed)
 *   • webpack alias "maplibre-gl$" → node_modules/maplibre-gl/dist/maplibre-gl.mjs
 *   • resolve.extensions includes ".mjs"
 *   • CSS rule /\.css$/ with ["style-loader", "css-loader"]
 */
const MapLibreMap: React.FC<MapLibreMapProps> = ({
  latitude,
  longitude,
  zoom = 4,
  height = 500,
  label,
  homeLatitude,
  homeLongitude,
  homeZoom,
  markers = [],
  highlightedMarkerId,
  onMarkerPress,
  onMarkerHover,
  onMarkerLeave,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef          = useRef<MapLibreMapClass | null>(null);
  const markersRef      = useRef<globalThis.Map<string, Marker>>(new globalThis.Map());
  const markerDataRef   = useRef<MapMarker[]>(markers);
  const hoverCallbacksRef = useRef<{
    onMarkerHover?: MapLibreMapProps['onMarkerHover'];
    onMarkerLeave?: MapLibreMapProps['onMarkerLeave'];
  }>({ onMarkerHover: undefined, onMarkerLeave: undefined });
  const resolvedHomeLatitude = homeLatitude ?? latitude;
  const resolvedHomeLongitude = homeLongitude ?? longitude;
  const resolvedHomeZoom = homeZoom ?? zoom;
  const homeCenterRef   = useRef<[number, number]>([resolvedHomeLongitude, resolvedHomeLatitude]);
  const homeZoomRef     = useRef<number>(resolvedHomeZoom);
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    markerDataRef.current = markers;
  }, [markers]);

  useEffect(() => {
    hoverCallbacksRef.current = { onMarkerHover, onMarkerLeave };
  }, [onMarkerHover, onMarkerLeave]);

  useEffect(() => {
    homeCenterRef.current = [resolvedHomeLongitude, resolvedHomeLatitude];
    homeZoomRef.current = resolvedHomeZoom;
  }, [resolvedHomeLatitude, resolvedHomeLongitude, resolvedHomeZoom]);

  const zoomMap = (direction: 1 | -1) => {
    if (!mapRef.current) return;
    const currentZoom = mapRef.current.getZoom();
    mapRef.current.easeTo({ zoom: currentZoom + direction, essential: true });
  };

  const resetMap = () => {
    if (!mapRef.current) return;
    mapRef.current.easeTo({
      center: homeCenterRef.current,
      zoom: homeZoomRef.current,
      essential: true,
    });
  };

  // ── EFFECT 1: initialise map once on mount ──────────────────────────────
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let cleanupListeners: () => void = () => {};

    // setTimeout(100) guarantees the <div> container has real pixel dimensions
    // before MapLibre reads them.
    const timerId = setTimeout(() => {
      const container = mapContainerRef.current;
      if (!container) { setMapError('Map container not found'); return; }

      try {
        const map = new MapLibreMapClass({
          container,
          style: {
            version: 8,
            name: 'OSM Raster',
            sources: {
              'osm-raster': {
                type: 'raster',
                tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
                tileSize: 256,
                attribution:
                  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
              },
            },
            layers: [{
              id: 'osm-raster-layer',
              type: 'raster',
              source: 'osm-raster',
              minzoom: 0,
              maxzoom: 19,
            }],
          } as StyleSpecification,
          center: [longitude, latitude], // MapLibre order: [lng, lat]
          zoom,
        });

        mapRef.current = map;
        setMapError(null);

        const handleMouseLeave = () => {
          hoverCallbacksRef.current.onMarkerLeave?.();
        };
        container.addEventListener('mouseleave', handleMouseLeave);
        cleanupListeners = () => {
          container.removeEventListener('mouseleave', handleMouseLeave);
        };

        map.on('load', () => {
          markersRef.current = addMapMarkers(
            map,
            markers,
            highlightedMarkerId,
            onMarkerPress,
            onMarkerHover,
            onMarkerLeave,
          );
        });
      } catch (e: any) {
        setMapError(e?.message ?? 'Failed to initialise map');
      }
    }, 100);

    return () => {
      clearTimeout(timerId);
      cleanupListeners();
      // Cleanup: marker.remove() BEFORE map.remove() (MapLibre v4+ requirement)
      markersRef.current.forEach(m => m.remove());
      markersRef.current.clear();
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — EFFECTs 2 & 3 handle all post-mount prop changes

  // ── EFFECT 2: smooth flyTo when center / zoom props change ──────────────
  useEffect(() => {
    if (Platform.OS !== 'web' || !mapRef.current) return;
    try {
      mapRef.current.flyTo({ center: [longitude, latitude], zoom, essential: true });
    } catch { /* map may not be fully initialised yet */ }
  }, [latitude, longitude, zoom]);

  // ── EFFECT 3: re-render markers when the markers array changes ──────────
  useEffect(() => {
    if (Platform.OS !== 'web' || !mapRef.current) return;
    const refresh = () => {
      if (!mapRef.current) return;
      markersRef.current.forEach(m => m.remove());
      markersRef.current.clear();
      markersRef.current = addMapMarkers(
        mapRef.current,
        markers,
        highlightedMarkerId,
        onMarkerPress,
        onMarkerHover,
        onMarkerLeave,
      );
    };
    if (mapRef.current.loaded()) { refresh(); }
    else { mapRef.current.once('load', refresh); }
  }, [highlightedMarkerId, markers, onMarkerPress, onMarkerHover, onMarkerLeave]);

  // ── Native fallback ─────────────────────────────────────────────────────
  if (Platform.OS !== 'web') {
    return (
      <View style={[styles.fallback, { height }]}>
        <Text style={styles.fallbackText}>
          Interactive map is only available on Web.
        </Text>
        <Text style={styles.fallbackSub}>
          Use @maplibre/maplibre-react-native for native platforms.
        </Text>
      </View>
    );
  }

  // ── Web render ───────────────────────────────────────────────────────────
  return (
    <View style={styles.wrapper}>
      {label != null && <Text style={styles.label}>{label}</Text>}
      {mapError ? (
        <View style={[styles.errorBox, { height }]}>
          <Text style={styles.errorTitle}>Map unavailable</Text>
          <Text style={styles.errorText}>{mapError}</Text>
        </View>
      ) : null}
      <View pointerEvents="box-none" style={styles.controlsOverlay}>
        <View style={styles.controlsStack}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Zoom in"
            onPress={() => zoomMap(1)}
            style={styles.controlButton}
          >
            <Text style={styles.controlButtonText}>+</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Zoom out"
            onPress={() => zoomMap(-1)}
            style={styles.controlButton}
          >
            <Text style={styles.controlButtonText}>−</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Reset map view"
            onPress={resetMap}
            style={styles.homeButton}
          >
            <MaterialIcons name="home" size={18} color="#034895" />
          </Pressable>
        </View>
      </View>
      {/* @ts-ignore — <div> is a DOM element; not in React Native type definitions */}
      <div
        ref={mapContainerRef}
        style={{
          width: '100%',
          height: `${height}px`,
          overflow: 'hidden',
          position: 'relative',
          display: mapError ? 'none' : 'block',
        }}
      />
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: { width: '100%' as any, overflow: 'hidden' as any, position: 'relative' as any },
  label: { fontSize: 13, fontWeight: '600', color: '#2D2D2D', marginBottom: 8, paddingHorizontal: 16 },
  controlsOverlay: {
    position: 'absolute' as any,
    top: 12,
    right: 12,
    zIndex: 20,
  },
  controlsStack: {
    gap: 6,
  },
  controlButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.12)',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
  },
  controlButtonText: { fontSize: 20, lineHeight: 22, fontWeight: '700', color: '#034895' },
  homeButton: {
    width: 34,
    height: 34,
    paddingHorizontal: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#034895',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
  },
  fallback: { width: '100%' as any, alignItems: 'center', justifyContent: 'center', backgroundColor: '#EAF2FA', borderRadius: 8, gap: 8, paddingHorizontal: 24 },
  fallbackText: { fontSize: 14, fontWeight: '600', color: '#616161', textAlign: 'center' },
  fallbackSub: { fontSize: 12, color: '#838383', textAlign: 'center' },
  errorBox: { width: '100%' as any, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFF3E0', borderRadius: 8, gap: 6, paddingHorizontal: 24 },
  errorTitle: { fontSize: 13, fontWeight: '700', color: '#995211' },
  errorText: { fontSize: 12, color: '#616161', textAlign: 'center' },
});

export default MapLibreMap;

